import React, { createContext, useContext, useState, ReactNode, useCallback } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import { t } from "../Translations";

// Define the possible outcomes
export type ConfirmResult = "yes" | "no" | "cancel" | "ok";

// Define the buttons that can be included in a confirm request
type ConfirmRequest = {
    message: string;
    title?: string;
    resolve: (result: ConfirmResult) => void;
    buttons: ("yes" | "no" | "cancel" | "ok")[]; // Configuration for buttons to be shown
};

export const ConfirmContext = createContext<{
    confirm: (message: string, title?: string) => Promise<boolean>;
    advancedConfirm: (message: string, title?: string, buttons?: ("yes" | "no" | "cancel")[]) => Promise<ConfirmResult>;
}>({
    confirm: () => {
        throw new Error("useConfirm must be used within a ConfirmProvider");
    },
    advancedConfirm: () => {
        throw new Error("useConfirm must be used within a ConfirmProvider");
    },
});

// Expose a global confirm function
export let globalConfirm: (message: string, title?: string) => Promise<boolean> = () => {
    throw new Error("Confirm function is not yet initialized.");
};

export let globalConfirmAdvanced: (message: string, title?: string, buttons?: ("yes" | "no" | "cancel")[])
    => Promise<ConfirmResult> = () => {
        throw new Error("Confirm function is not yet initialized.");
    };

export function ConfirmProvider({ children }: { children: ReactNode }) {
    const [queue, setQueue] = useState<ConfirmRequest[]>([]);
    const [current, setCurrent] = useState<ConfirmRequest | null>(null);

    const processNext = useCallback(() => {
        setCurrent(null);
        setQueue((prev) => {
            const [, ...rest] = prev;
            if (rest.length > 0) setCurrent(rest[0]);
            return rest;
        });
    }, []);

    const confirm = (message: string, title = t("Bekräfta")): Promise<boolean> => {
        return new Promise((resolve) => {
            const request: ConfirmRequest = { message, title, resolve: (result) => resolve(result === "ok"), buttons: ["ok", "cancel"] };
            setQueue((prev) => {
                const newQueue = [...prev, request];
                if (!current) {
                    setCurrent(request); // Show immediately if nothing is showing
                }
                return newQueue;
            });
        });
    };
    // Set the globalConfirm function when the context is available
    globalConfirm = confirm;

    const advancedConfirm = (message: string, title = "Confirm", buttons: ("yes" | "no" | "cancel")[] = ["yes", "no", "cancel"]): Promise<ConfirmResult> => {
        return new Promise((resolve) => {
            const request: ConfirmRequest = { message, title, resolve, buttons };
            setQueue((prev) => {
                const newQueue = [...prev, request];
                if (!current) {
                    setCurrent(request); // Show immediately if nothing is showing
                }
                return newQueue;
            });
        });
    };
    globalConfirmAdvanced = advancedConfirm;

    const handleResult = (result: ConfirmResult) => {
        if (current) {
            current.resolve(result);
            processNext();
        }
    };

    return (
        <ConfirmContext.Provider value={{ confirm, advancedConfirm }}>
            {children}
            {current && (
                <Modal
                    show={true}
                    backdrop="static"
                    keyboard={true}
                    onHide={() => handleResult("cancel")}
                >
                    <Modal.Header closeButton>
                        <Modal.Title>{current.title}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>{current.message}</Modal.Body>
                    <Modal.Footer>
                        {current.buttons.includes("cancel") && (
                            <Button variant="secondary" onClick={() => handleResult("cancel")}>
                                {t('Avbryt')}
                            </Button>
                        )}
                        {current.buttons.includes("no") && (
                            <Button variant="danger" onClick={() => handleResult("no")}>
                                {t('Nej')}
                            </Button>
                        )}
                        {current.buttons.includes("yes") && (
                            <Button variant="primary" onClick={() => handleResult("yes")}>
                                {t('Ja')}
                            </Button>
                        )}
                        {current.buttons.includes("ok") && (
                            <Button variant="primary" onClick={() => handleResult("ok")}>
                                {t('OK')}
                            </Button>
                        )}
                    </Modal.Footer>
                </Modal>
            )}
        </ConfirmContext.Provider>
    );
}

// Directly access ConfirmContext
export const useConfirmContext = () => useContext(ConfirmContext);
